import { useMemo, useState } from 'react';
import { Trophy, Loader2 } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ContestCard from '../components/contest/ContestCard';
import FooterAdvert, { FOOTER_ADVERT_RESERVED_SPACE } from '../components/adverts/FooterAdvert';
import { CARD_LIST_GRID } from '../utils/desktopColumn';
import SquareResultsModal from '../components/modals/SquareResultsModal';
import { useGetSquareQuery, type SquaresContestResponse } from '../api/squares/getSquares';
import { useGetMyPicksQuery } from '../api/squares/getMyPicks';
import { useGetFixtureScoresBatchQuery } from '../api/squares/getFixtureScores';
import { useGetMySelectionQuery } from '../api/squares/getMySelection';
import { useGetSquaresOptionsQuery } from '../api/squares/getSquaresOptions';
import { useGetWinningSquaresQuery } from '../api/squares/getWinningSquares';
import { usePaginatedScroll } from '@low6-pickem/ui';
import { format } from 'date-fns';
import { appendLobbyParams } from '@/utils/lobbyNavigation';

const isCompletedContest = (contest: SquaresContestResponse) => {
  const statusUpper = contest.status?.toUpperCase?.() ?? '';
  return statusUpper === 'COMPLETE' || contest.status === 'final' || contest.status === 'completed';
};

const mapSportToLeague = (sport: string): 'NFL' | 'NBA' | 'NHL' | 'MLB' | undefined => {
  const sportMap: Record<string, 'NFL' | 'NBA' | 'NHL' | 'MLB'> = {
    NFL: 'NFL',
    NBA: 'NBA',
    NHL: 'NHL',
    MLB: 'MLB',
    NCAAMB: 'NBA', // college basketball → NBA stat family
    NCAAFB: 'NFL', // college football → NFL stat family (DKPLAT-673)
  };
  return sportMap[sport] || undefined;
};

const formatStartTime = (startTimeUtc?: string) => {
  if (!startTimeUtc) return 'TBD';
  try {
    return format(new Date(startTimeUtc), 'MMM d, h:mm a');
  } catch {
    return 'TBD';
  }
};

/**
 * Results View (Recent)
 * Shows completed contests and results
 */
export default function ResultsView() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [selectedContestId, setSelectedContestId] = useState<string | null>(null);

  // Results: only completed/final contests that THIS user has entered (from my-picks).
  const {
    data: myPicksRaw = [],
    isLoading: myPicksLoading,
    error: myPicksError,
  } = useGetMyPicksQuery();

  const completedContests = useMemo(() => {
    const completed = (myPicksRaw as SquaresContestResponse[]).filter(isCompletedContest);
    return completed.sort((a, b) => {
      const ta = a.start_time_utc ? new Date(a.start_time_utc).getTime() : 0;
      const tb = b.start_time_utc ? new Date(b.start_time_utc).getTime() : 0;
      return tb - ta;
    });
  }, [myPicksRaw]);

  const {
    visibleItems: visibleCompletedContests,
    sentinelRef,
    hasMore,
    previousCount,
  } = usePaginatedScroll(completedContests);

  const completedContestIds = useMemo(
    () => visibleCompletedContests.map(c => c.squares_contest_id).filter(Boolean),
    [visibleCompletedContests]
  );
  const { data: fixtureScoresBatch } = useGetFixtureScoresBatchQuery(completedContestIds);
  const fixtures = fixtureScoresBatch?.fixtures || {};

  const handleViewResults = (contestId: string) => {
    // Open modal first
    setSelectedContestId(contestId);
    setShowResultsModal(true);
  };

  const handleViewGame = (contestId: string) => {
    navigate(appendLobbyParams(`/play?contestId=${contestId}&mode=results`, searchParams));
  };

  const handleCloseModal = () => {
    setShowResultsModal(false);
    setSelectedContestId(null);
  };

  // Get selected contest data for modal from list (fallback) - search full list so modal works for any contest
  const selectedContestFromList = completedContests.find(
    contest => contest.squares_contest_id === selectedContestId
  );

  // Fetch full contest details when modal is open (same as PlayView does)
  // This ensures we get all fields including badge_image_url and jersey URLs
  const { data: fullContestDetails } = useGetSquareQuery(
    showResultsModal && selectedContestId ? selectedContestId : null
  );

  // Use full contest details if available, otherwise fall back to list data
  const selectedContest = fullContestDetails || selectedContestFromList;

  // Get contest ID for fetching user data (use question_id if available, otherwise squares_contest_id)
  const contestId = selectedContest?.question_id || selectedContest?.squares_contest_id;

  // Fetch user's selection data to get prize amounts (only when modal is open and contest is selected)
  const { data: selectionData } = useGetMySelectionQuery(
    showResultsModal && contestId ? contestId : null
  );

  // Fetch options to get total entries and user entry count (only when modal is open and contest is selected)
  const { data: optionsData } = useGetSquaresOptionsQuery(
    showResultsModal && selectedContest?.question_id ? selectedContest.question_id : null
  );

  // Fetch winning squares data to determine which periods the user won (only when modal is open)
  const { data: winningSquaresData } = useGetWinningSquaresQuery(
    showResultsModal && selectedContestId ? selectedContestId : null
  );

  const isLoading = myPicksLoading;
  const error = myPicksError;

  if (isLoading) {
    return (
      <div className="px-4 py-8 flex flex-col items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#53d337] mb-4" />
        <p className="text-zinc-400">Loading results...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4 py-4 pb-8">
        <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-4">
          <p className="text-red-400 font-semibold">Error loading results</p>
          <p className="text-red-300 text-sm mt-1">
            {error instanceof Error ? error.message : 'Failed to load results'}
          </p>
        </div>
      </div>
    );
  }

  // Empty state
  if (completedContests.length === 0) {
    return (
      <div className="px-4 py-8 flex flex-col items-center justify-center min-h-[400px]">
        <div className="w-20 h-20 rounded-full bg-zinc-800 flex items-center justify-center mb-4">
          <Trophy className="w-10 h-10 text-zinc-600" />
        </div>
        <h2 className="text-white font-semibold text-lg mb-2">No Results Yet</h2>
        <p className="text-zinc-500 text-sm text-center max-w-xs">
          Results will appear here once contests you've entered have completed.
        </p>
      </div>
    );
  }

  return (
    <div className="px-4 py-4" style={{ paddingBottom: FOOTER_ADVERT_RESERVED_SPACE }}>
      {/* Results List: 6 at a time, load more on scroll */}
      <div className={CARD_LIST_GRID}>
        {visibleCompletedContests.map((contest, index) => {
          const title = contest.contest_name_en || contest.contest_name_fr || 'Squares Contest';
          const entryFee = contest.entry_fee === 0 ? 'FREE' : `$${contest.entry_fee.toFixed(2)}`;
          const prizePool = `$${contest.total_prize_amount?.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',') || '0.00'}`;
          const entries =
            contest.square_count ||
            (contest.grid_rows && contest.grid_cols ? contest.grid_rows * contest.grid_cols : 100);
          const entriesTaken = contest.total_entries_to_date || 0;
          const fixture = fixtures[String(contest.squares_contest_id)] || null;

          const rowTeamId = contest.row_team_id || undefined;
          const colTeamId = contest.col_team_id || undefined;
          const homeId = fixture?.homeTeam?.id;
          const awayId = fixture?.awayTeam?.id;
          const homeScore = fixture?.homeScore ?? 0;
          const awayScore = fixture?.awayScore ?? 0;
          const rowScore =
            rowTeamId && homeId && rowTeamId === homeId
              ? homeScore
              : rowTeamId && awayId && rowTeamId === awayId
                ? awayScore
                : homeScore;
          const colScore =
            colTeamId && awayId && colTeamId === awayId
              ? awayScore
              : colTeamId && homeId && colTeamId === homeId
                ? homeScore
                : awayScore;

          // Apply fade-in animation to newly loaded items
          const isNewlyLoaded = index >= previousCount && previousCount > 0;

          return (
            <div
              key={contest.squares_contest_id}
              className={isNewlyLoaded ? 'contest-card-fade-in' : ''}
            >
              <ContestCard
                id={contest.squares_contest_id}
                title={title}
                startTime={formatStartTime(contest.start_time_utc)}
                startDateTime={contest.start_time_utc}
                gameCount={1}
                entries={entries}
                entriesTaken={entriesTaken}
                entryPrice={entryFee}
                prizePool={prizePool}
                league={mapSportToLeague(contest.sport)}
                sport={contest.sport}
                status="completed"
                isGameFinal={true}
                liveData={
                  fixture
                    ? {
                        team1Name: contest.row_team_display_name || 'Team 1',
                        team1Score: rowScore,
                        team2Name: contest.col_team_display_name || 'Team 2',
                        team2Score: colScore,
                        totalWinnings: `$${(contest.total_winnings ?? 0).toFixed(2)}`,
                      }
                    : undefined
                }
                hasWinnings={(contest.total_winnings ?? 0) > 0}
                onClick={() => handleViewGame(contest.squares_contest_id)}
                onViewResults={() => handleViewResults(contest.squares_contest_id)}
                onViewGame={() => handleViewGame(contest.squares_contest_id)}
              />
            </div>
          );
        })}
        {hasMore && <div ref={sentinelRef} className="h-4" aria-hidden />}
      </div>

      {/* Footer Advert */}
      <FooterAdvert />

      {/* Square Results Modal */}
      {selectedContest &&
        (() => {
          // Calculate user statistics from fetched data
          const totalUsers = optionsData?.total_entries || 0;
          const userSquareNumber = (selectionData as any)?.selection?.square_number;
          const userEntryCount =
            userSquareNumber && optionsData
              ? optionsData.options.find(opt => opt.square_number === userSquareNumber)
                  ?.entry_count || 0
              : 0;
          const userPercentage = totalUsers > 0 ? (userEntryCount / totalUsers) * 100 : 0;

          // Get period codes from contest periods or use defaults
          const periods = selectedContest.periods || [];
          const periodCodes =
            periods.length > 0
              ? periods.map((p: any) => p.periodCode || p.period_code || 'Q1')
              : ['Q1', 'Q2', 'Q3', 'FINAL']; // Default periods

          // Get winning squares by period and graded period scores
          const winningSquaresByPeriod = winningSquaresData?.winningSquaresByPeriod || {};
          const periodsWithScores = winningSquaresData?.periods || [];

          // Build map of periodCode -> { rowTeamScore, colTeamScore }
          const periodScoresMap = new Map<string, { rowTeamScore: number; colTeamScore: number }>();
          for (const p of periodsWithScores) {
            const code = p.periodCode || (p as any).period_code;
            if (code != null) {
              periodScoresMap.set(code, {
                rowTeamScore: p.rowTeamScore ?? 0,
                colTeamScore: p.colTeamScore ?? 0,
              });
            }
          }

          // Calculate prize amounts for each period
          // If the user's square won a period, calculate prize per winner
          const potentialWinnings = periodCodes.map((periodCode: string) => {
            const winningSquareForPeriod = winningSquaresByPeriod[periodCode];
            const userWonThisPeriod =
              userSquareNumber !== null && winningSquareForPeriod === userSquareNumber;

            let prizeAmount = 0;

            if (userWonThisPeriod && optionsData) {
              // Find the period config to get total prize amount
              const periodConfig = periods.find(
                (p: any) => (p.periodCode || p.period_code) === periodCode
              );
              const totalPrizeForPeriod = periodConfig?.prizeAmount
                ? parseFloat(String(periodConfig.prizeAmount))
                : 0;

              // Find how many entries are on the winning square (number of winners)
              const winningSquareOption = optionsData.options.find(
                opt => opt.square_number === winningSquareForPeriod
              );
              const winnersCount = winningSquareOption?.entry_count || 1; // Avoid division by zero

              // Calculate prize per winner
              prizeAmount = winnersCount > 0 ? totalPrizeForPeriod / winnersCount : 0;
            }

            const formattedAmount = `$${prizeAmount.toFixed(2)}`;

            // Format period code for display (Q1, Q2, Q3, FT)
            const displayCode = periodCode === 'FINAL' ? 'FT' : periodCode;

            // Get graded scores for this period (row=team1, col=team2)
            const scores = periodScoresMap.get(periodCode);

            return {
              quarter: displayCode,
              amount: formattedAmount,
              team1Score: scores?.rowTeamScore,
              team2Score: scores?.colTeamScore,
            };
          });

          // Get fixture for scores
          const fixture = fixtures[String(contestId)] || null;
          const rowTeamId = selectedContest.row_team_id;
          const colTeamId = selectedContest.col_team_id;
          const homeId = fixture?.homeTeam?.id;
          const awayId = fixture?.awayTeam?.id;
          const homeScore = fixture?.homeScore ?? 0;
          const awayScore = fixture?.awayScore ?? 0;
          const rowScore =
            rowTeamId && homeId && rowTeamId === homeId
              ? homeScore
              : rowTeamId && awayId && rowTeamId === awayId
                ? awayScore
                : homeScore;
          const colScore =
            colTeamId && awayId && colTeamId === awayId
              ? awayScore
              : colTeamId && homeId && colTeamId === homeId
                ? homeScore
                : awayScore;

          return (
            <SquareResultsModal
              contestId={contestId}
              isOpen={showResultsModal}
              onClose={handleCloseModal}
              team1Name={selectedContest.row_team_display_name}
              team1JerseyUrl={selectedContest.row_team_icon_url}
              team2Name={selectedContest.col_team_display_name}
              team2JerseyUrl={selectedContest.col_team_icon_url}
              team1Score={rowScore}
              team2Score={colScore}
              sponsorLogoUrl={selectedContest.badge_image_url}
              userCount={userEntryCount}
              userPercentage={userPercentage}
              totalUsers={totalUsers}
              potentialWinnings={potentialWinnings}
              userSquareRowDigit={
                userSquareNumber &&
                selectedContest.row_digits &&
                selectedContest.row_digits.length === 10
                  ? selectedContest.row_digits[Math.floor((userSquareNumber - 1) / 10)]
                  : undefined
              }
              userSquareColDigit={
                userSquareNumber &&
                selectedContest.col_digits &&
                selectedContest.col_digits.length === 10
                  ? selectedContest.col_digits[(userSquareNumber - 1) % 10]
                  : undefined
              }
              sport={selectedContest.sport}
              isCompleted
            />
          );
        })()}
    </div>
  );
}
